import type { TemplateFormTranslations } from "@/lib/i18n/types/documents-sections/templateForm";
import type { DocumentTemplateId } from "./documentTemplates";

/** Section and field ids are copy keys, so a schema cannot reference missing copy. */
export type TemplateFormSectionId = keyof TemplateFormTranslations["sections"];
export type TemplateFormFieldId = keyof TemplateFormTranslations["fields"];
export type WeatherOptionId = keyof TemplateFormTranslations["weather"];

/** Values Franklin already knows, filled in when the form opens. */
export type TemplateFormPrefillKey =
  | "today"
  | "organisation"
  | "buildingName"
  | "buildingAddress"
  | "registryCode"
  | "author";

export type TemplateFormFieldType =
  | "text"
  | "textarea"
  | "date"
  | "time"
  | "number"
  | "weather"
  | "people"
  | "attachments";

export interface TemplateFormFieldDef {
  id: TemplateFormFieldId;
  type: TemplateFormFieldType;
  /** Fills the field on open from the building / org / user we already have. */
  prefill?: TemplateFormPrefillKey;
  /** Short fields pair up two-per-row; long ones take the full column. */
  compact?: boolean;
  /** The form cannot be filed without it. Kept to what the document is
   *  meaningless without — an act with no description of the covered works is
   *  not an act — rather than every field an official form has a box for. */
  required?: boolean;
  /** Renders an Import button that fills this field from a form already filed
   *  in the project. The source is named explicitly because it is often a
   *  different template and a differently-named field — a diary entry imports
   *  the works from a covered-works act, not from a previous diary. */
  importFrom?: { templateId: DocumentTemplateId; fieldId: TemplateFormFieldId };
  /** Person rows: whether the certificate-number column is shown. */
  withCertificate?: boolean;
  /**
   * The row this field occupies on the statutory form, matching the official
   * layout exactly (the act runs 1–11, the diary 1–14, with rows 1 and 2 being
   * the header's company and date cells).
   *
   * A field WITHOUT one is Franklin's own addition. Those still print, but in a
   * separate block below the statutory table rather than renumbering it — a
   * filed act has to stay the document the reader expects, and inserting our
   * rows into the middle would shift every number after them.
   */
  statutoryRow?: number;
}

export interface TemplateFormSectionDef {
  id: TemplateFormSectionId;
  fields: TemplateFormFieldDef[];
}

/** The parts of the printed form that are not ordinary label/value rows. */
export interface TemplateFormPrintDef {
  /** Row 3 on both statutory forms is a single cell reading
   *  "<building>, <address> / <contract no>" — three inputs, one printed row. */
  siteAndContractRow: number;
  /** The diary's weather row is a 7-column grid, not a value cell. */
  weatherRow?: number;
}

export interface TemplateFormDef {
  /** The numbering key: `<slug>-nr-<n>` is what the client scans to seed the
   *  next number. Stays Estonian whatever the document prints in, so the
   *  sequence does not fork per language — the authoritative number comes from
   *  `next_template_form_number` at save time regardless. */
  slug: string;
  /** Base of the auto-generated document name, per printed language. */
  nameSlug: { et: string; en: string };
  /** Which number the PDF prints top-right: an act is numbered, a diary paginated. */
  numberLabel: TemplateFormNumberLabel;
  /** How the statutory table is laid out, beyond its plain rows. */
  print?: TemplateFormPrintDef;
  sections: TemplateFormSectionDef[];
}

export type TemplateFormNumberLabel = keyof TemplateFormTranslations["pdf"]["numberLabels"];

/** The options the statutory diary prints across its weather row, in order. */
export const WEATHER_OPTIONS: WeatherOptionId[] = ["strongWind", "dry", "rain", "sleet", "snow"];

/** The general section every template opens with — same fields, same order. */
const generalSection = (fields: TemplateFormFieldDef[]): TemplateFormSectionDef => ({
  id: "general",
  fields: [
    // Rows 1–3 of the printed form: the header's company and date cells, and
    // the composite site/contract row.
    { id: "date", type: "date", prefill: "today", compact: true, required: true },
    { id: "buildingName", type: "text", prefill: "buildingName", compact: true, required: true },
    { id: "buildingAddress", type: "text", prefill: "buildingAddress" },
    ...fields,
  ],
});

const signatureSection = (
  contractorRow: number,
  supervisorRow: number
): TemplateFormSectionDef => ({
  id: "parties",
  fields: [
    // Two separate blocks, not one list with a flag: the statutory form asks
    // for the builder and the owner's supervisor by name, each with their own
    // "Nimi, ettevõte / Kutsetunnistus" sub-table.
    {
      id: "contractorSignatories",
      type: "people",
      withCertificate: true,
      statutoryRow: contractorRow,
    },
    {
      id: "supervisorSignatories",
      type: "people",
      withCertificate: true,
      statutoryRow: supervisorRow,
    },
  ],
});

/**
 * The form each template renders. The engine reads only this — layout,
 * behaviour and validation are shared, so a new template is a data entry.
 *
 * `statutoryRow` numbers follow the official Estonian forms exactly; fields
 * without one are Franklin's additions and print below the statutory table.
 */
export const TEMPLATE_FORMS: Record<DocumentTemplateId, TemplateFormDef> = {
  coveredWorksAct: {
    slug: "kaetud-toode-akt",
    nameSlug: { et: "kaetud-toode-akt", en: "covered-works-act" },
    numberLabel: "act",
    print: { siteAndContractRow: 3 },
    sections: [
      generalSection([
        { id: "contractNumber", type: "text", compact: true },
        { id: "organisation", type: "text", prefill: "organisation", compact: true },
      ]),
      {
        id: "coveredWorks",
        fields: [
          {
            id: "worksDescription",
            type: "textarea",
            required: true,
            statutoryRow: 4,
            importFrom: { templateId: "coveredWorksAct", fieldId: "worksDescription" },
          },
          { id: "conformityAssessment", type: "textarea", statutoryRow: 5 },
          { id: "otherWorkData", type: "textarea", statutoryRow: 6 },
          { id: "inspectionMethod", type: "textarea", statutoryRow: 7 },
          { id: "inspectionTime", type: "time", compact: true, statutoryRow: 8 },
          // Franklin's own — no box on the statutory act.
          { id: "worksLocation", type: "text" },
          { id: "worksStart", type: "date", compact: true },
          { id: "worksEnd", type: "date", compact: true },
          { id: "materials", type: "textarea" },
          { id: "standards", type: "text" },
        ],
      },
      {
        id: "attachments",
        fields: [{ id: "attachments", type: "attachments", statutoryRow: 9 }],
      },
      signatureSection(10, 11),
    ],
  },
  constructionDiary: {
    slug: "ehituspaevik",
    nameSlug: { et: "ehituspaevik", en: "construction-diary" },
    numberLabel: "page",
    print: { siteAndContractRow: 3, weatherRow: 5 },
    sections: [
      generalSection([
        { id: "contractNumber", type: "text", compact: true },
        { id: "organisation", type: "text", prefill: "organisation", compact: true },
        { id: "author", type: "text", prefill: "author", compact: true, statutoryRow: 4 },
      ]),
      {
        id: "conditions",
        fields: [
          // Row 5 prints as one grid: time and temperature down the left, the
          // weather options across the top.
          { id: "inspectionTime", type: "time", compact: true },
          { id: "temperature", type: "number", compact: true },
          { id: "weather", type: "weather" },
          { id: "otherFactors", type: "textarea", statutoryRow: 6 },
        ],
      },
      {
        id: "diaryWorks",
        fields: [
          {
            id: "worksPerformed",
            type: "textarea",
            required: true,
            statutoryRow: 7,
            importFrom: { templateId: "coveredWorksAct", fieldId: "worksDescription" },
          },
          { id: "materials", type: "textarea", statutoryRow: 8 },
          { id: "coveredWorksActs", type: "textarea", statutoryRow: 9 },
          { id: "wasteRemovalDate", type: "date", compact: true, statutoryRow: 10 },
          // Franklin's own.
          { id: "workforce", type: "number", compact: true },
          { id: "machinery", type: "textarea" },
          { id: "deliveries", type: "textarea" },
        ],
      },
      {
        id: "diaryNotes",
        fields: [
          { id: "supervisorNote", type: "textarea", statutoryRow: 11 },
          { id: "stateSupervisionOrder", type: "textarea", statutoryRow: 12 },
          { id: "notes", type: "textarea" },
          { id: "instructions", type: "textarea" },
        ],
      },
      { id: "attachments", fields: [{ id: "attachments", type: "attachments" }] },
      signatureSection(13, 14),
    ],
  },
  moreFranklinTemplates: {
    slug: "franklin-vorm",
    nameSlug: { et: "franklin-vorm", en: "franklin-form" },
    numberLabel: "document",
    sections: [
      generalSection([]),
      { id: "attachments", fields: [{ id: "attachments", type: "attachments" }] },
    ],
  },
};
